/**
 * Pharmacy products waiting for the medical director (Treatment Plan spec v1.4,
 * S-21, PS-010). The client, 8 October: "Dr. Joe confirms them in his portal,
 * two business days is fine, and unconfirmed items stay hidden from clinics."
 *
 * Each new SKU comes with the suggested MCOS drug, the reasons, and the amount
 * in the drug's usage unit worked out where it can be. Confirm, change or
 * reject. A confirmed SKU matches automatically on every later upload.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, PageHeader, inputClass } from '../components/ui';

interface Suggestion { itemId: string; name: string; score: number | null; reasons: string[]; usageUnit: string | null; blocked: boolean; amount: number | null; working: string | null }
interface Waiting {
  pharmacyId: string;
  pharmacyName: string;
  sku: string;
  product: string;
  form: string | null;
  container: string | null;
  priceBasis: string | null;
  priceCents: number | null;
  waitingSince: string;
  dueBy: string;
  overdue: boolean;
  suggestions: Suggestion[];
}
interface Choice { itemId: string; name: string; usageUnit: string | null; blocked: boolean }

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
const when = (iso: string) => new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

function WaitingCard({ w, choices }: { w: Waiting; choices: Choice[] }) {
  const qc = useQueryClient();
  const first = w.suggestions[0];
  const [itemId, setItemId] = useState(first?.itemId ?? '');
  const picked = w.suggestions.find((s) => s.itemId === itemId);
  const unit = picked?.usageUnit ?? choices.find((c) => c.itemId === itemId)?.usageUnit ?? null;
  const [amount, setAmount] = useState(first?.amount != null ? String(first.amount) : '');
  const [note, setNote] = useState('');
  const [changing, setChanging] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pharmacy-products'] });
    void qc.invalidateQueries({ queryKey: ['pharmacy-products-count'] });
  };
  const confirm = useMutation({ mutationFn: () => api.post('/clinical/pharmacy-mappings/confirm', { pharmacyId: w.pharmacyId, sku: w.sku, itemId, amountInUsageUnit: Number(amount), note: note || null }), onSuccess: refresh });
  const reject = useMutation({ mutationFn: () => api.post('/clinical/pharmacy-mappings/reject', { pharmacyId: w.pharmacyId, sku: w.sku, note: note || null }), onSuccess: refresh });
  const choose = (id: string) => {
    setItemId(id);
    const s = w.suggestions.find((x) => x.itemId === id);
    setAmount(s?.amount != null ? String(s.amount) : '');
  };
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#4d6072]">{w.pharmacyName}, {w.sku}</p>
          <p className="mt-1 font-display text-[19px] text-navy">{w.product}</p>
          <p className="mt-0.5 text-[13px] text-[#4d6072]">
            {[w.form, w.container ? `${w.container} per container` : null, w.priceCents != null ? `$${(w.priceCents / 100).toFixed(2)} ${w.priceBasis ?? ''}`.trim() : null].filter(Boolean).join(', ')}
          </p>
        </div>
        <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${w.overdue ? 'bg-alert-soft text-alert' : 'bg-[#fbf0d6] text-amber-deep'}`}>
          {w.overdue ? `Overdue since ${when(w.dueBy)}` : `Due ${when(w.dueBy)}`}
        </span>
      </div>

      <fieldset className="mt-4">
        <legend className="mb-2 text-xs font-semibold text-ink">Which MCOS drug is it?</legend>
        <div className="space-y-2">
          {w.suggestions.map((s) => (
            <label key={s.itemId} className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${itemId === s.itemId ? 'border-forest bg-mint' : 'border-hairline bg-white'}`}>
              <input type="radio" className="mt-1 accent-forest" name={`${w.pharmacyId}-${w.sku}`} checked={itemId === s.itemId} onChange={() => choose(s.itemId)} />
              <span className="min-w-0">
                <span className="font-semibold text-navy">{s.name}</span>
                {s.blocked && <span className="ml-2 rounded-full bg-[#eef0f3] px-2 py-0.5 text-[11px] font-semibold text-[#4d6072]">Blocked in MCOS</span>}
                <span className="block text-[13px] text-[#4d6072]">{s.reasons.join('. ')}.</span>
              </span>
            </label>
          ))}
        </div>
        {!changing ? (
          <button type="button" className="mt-2 text-[13px] font-semibold text-forest hover:underline" onClick={() => setChanging(true)}>A different drug</button>
        ) : (
          <div className="mt-2 max-w-[420px]">
            <Field label="Choose another MCOS drug">
              <select className={inputClass} value={w.suggestions.some((s) => s.itemId === itemId) ? '' : itemId} onChange={(e) => choose(e.target.value)}>
                <option value="">Choose</option>
                {choices.map((c) => <option key={c.itemId} value={c.itemId}>{c.name}{c.blocked ? ' (blocked)' : ''}</option>)}
              </select>
            </Field>
          </div>
        )}
      </fieldset>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <Field label={`Amount per container${unit ? `, in ${unit}` : ''}`} hint={picked?.working ?? 'From the label: the total in the drug\'s usage unit.'}>
          <input className={inputClass} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Note, if any"><input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      {(confirm.error || reject.error) && <p className="mt-3 text-sm text-red-600">{errorText(confirm.error ?? reject.error)}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button loading={confirm.isPending} disabled={!itemId || !(Number(amount) > 0)} onClick={() => confirm.mutate()}>Confirm</Button>
        <Button variant="secondary" loading={reject.isPending} onClick={() => reject.mutate()}>Not an MCOS drug</Button>
      </div>
    </Card>
  );
}

export function PharmacyProductsPage() {
  const data = useQuery({ queryKey: ['pharmacy-products'], queryFn: () => api.get<{ queue: Waiting[]; choices: Choice[] }>('/clinical/pharmacy-mappings') });
  return (
    <>
      <PageHeader title="Pharmacy products" subtitle="New products on pharmacy price sheets. Confirm which MCOS drug each one is within two business days. Until you do, clinics never see it." />
      {data.error ? (
        <ErrorState error={data.error} retry={() => void data.refetch()} />
      ) : !data.data ? (
        <LoadingRows rows={4} />
      ) : data.data.queue.length === 0 ? (
        <Card><EmptyState title="Nothing waiting" body="When a pharmacy publishes a price sheet with a product MCOS has not seen before, it appears here." /></Card>
      ) : (
        <div className="space-y-5">{data.data.queue.map((w) => <WaitingCard key={`${w.pharmacyId}:${w.sku}`} w={w} choices={data.data!.choices} />)}</div>
      )}
    </>
  );
}
